import { postJSON } from '../services/api'
import { message } from 'antd'
import { UploadFile } from 'antd'

export interface UploadedDocument {
  id: string
  name: string
  pdf: {
    bucket: string
    filename: string
    path: string
    url: string
  }
}

interface PresignResponse {
  data: {
    method: string
    url: string
    key: string
    expiresInSeconds: number
  }
}

interface CommitResponse {
  data: UploadedDocument
}

/**
 * Uploads a file using the presign/commit flow
 * @param file - The file to upload
 * @param residentId - The resident ID
 * @param categoryId - The category ID (Communication Records or Referrals)
 * @returns The uploaded document object
 */
export const uploadDocument = async (
  file: File,
  residentId: string,
  categoryId: string
): Promise<UploadedDocument> => {
  try {
    // Step 1: Get presigned URL
    const presignResponse = (await postJSON({
      url: '/api/list/documents/uploads/presign',
      method: 'POST',
      json: {
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        residentId,
        categoryId
      }
    })) as PresignResponse

    const { url, key, method } = presignResponse.data

    // Step 2: Upload directly to S3 using presigned URL
    const uploadResponse = await fetch(url, {
      method: method || 'PUT',
      body: file
    })

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text()
      console.error('Upload failed:', errorText)
      throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
    }

    // Step 3: Commit the upload
    const commitResponse = (await postJSON({
      url: '/api/list/documents/uploads/commit',
      method: 'POST',
      json: {
        key,
        resident: residentId,
        name: file.name,
        category: categoryId
      }
    })) as CommitResponse

    return commitResponse.data
  } catch (error) {
    console.error('Document upload failed:', error)
    message.error('Failed to upload document. Please try again.')
    throw error
  }
}

/**
 * Uploads multiple files using the presign/commit flow
 * @param files - Array of Ant Design UploadFile objects (max 3)
 * @param residentId - The resident ID
 * @param categoryId - The category ID
 * @returns Array of document IDs
 */
export const uploadDocuments = async (
  files: UploadFile[],
  residentId: string,
  categoryId: string
): Promise<string[]> => {
  const documentIds: string[] = []

  for (const uploadFile of files) {
    if (uploadFile.originFileObj) {
      try {
        const doc = await uploadDocument(
          uploadFile.originFileObj,
          residentId,
          categoryId
        )
        documentIds.push(doc.id)
      } catch (error) {
        // If one upload fails, show error but continue with others
        console.error(`Failed to upload ${uploadFile.name}:`, error)
      }
    }
  }

  if (documentIds.length === 0 && files.length > 0) {
    throw new Error('All document uploads failed')
  }

  return documentIds
}
